import {
  AppstoreOutlined,
  ArrowLeftOutlined,
  CheckCircleOutlined,
  CrownOutlined,
  LockOutlined,
  MailOutlined,
  SafetyCertificateOutlined,
  ShoppingCartOutlined,
  TeamOutlined,
  UserOutlined,
} from '@ant-design/icons';
import { Button, Form, Input, Switch } from 'antd';
import { useEffect, useMemo } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { ROUTES } from '@/constants/routes';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { ADMIN_MODULES } from '@/constants/adminModules';
import {
  useCreateAdminUserMutation,
  useGetAdminUserQuery,
  useGetPermissionsConfigQuery,
  useUpdateAdminUserMutation,
} from '@/modules/users/usersApi';
import { useAppSelector } from '@/store/hooks';
import type { AccessProfile, AdminUser, AdminUserRole } from '@/types/adminUser';
import { resolveMutationResult } from '@/utils/apiResponse';
import { ModuleAccessPicker } from './components/ModuleAccessPicker';
import styles from './UserForm.module.scss';

interface UserFormValues {
  firstName: string;
  lastName: string;
  email: string;
  role: AdminUserRole;
  password: string;
  confirmPassword: string;
  isActive: boolean;
  modulePermissions: string[];
  accessProfileId: string | null;
}

type RoleAssignment = 'super_admin' | string;

function profileIcon(name: string) {
  const lower = name.toLowerCase();
  if (lower.includes('product')) return AppstoreOutlined;
  if (lower.includes('sales')) return ShoppingCartOutlined;
  if (lower.includes('sub') || lower.includes('admin')) return SafetyCertificateOutlined;
  return TeamOutlined;
}

function getRoleDisplayLabel(
  role: AdminUserRole,
  accessProfileId: string | null,
  profiles: AccessProfile[],
): string {
  if (role === 'super_admin') return 'Super Admin';
  if (accessProfileId) {
    const profile = profiles.find((item) => item.id === accessProfileId);
    if (profile) return profile.name;
  }
  if (role === 'admin') return 'Admin';
  return 'Manager';
}

export default function UserForm() {
  const notify = useNotify();
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const currentUser = useAppSelector((store) => store.auth.user);

  const { data: user, isLoading: isLoadingUser } = useGetAdminUserQuery(id ?? '', {
    skip: !isEdit || !id,
  });
  const { data: permissionsConfig } = useGetPermissionsConfigQuery();
  const [createUser, createState] = useCreateAdminUserMutation();
  const [updateUser, updateState] = useUpdateAdminUserMutation();

  const isSaving = createState.isLoading || updateState.isLoading;
  useGlobalLoader(isSaving);
  useGlobalLoader(isEdit && isLoadingUser);

  const { control, handleSubmit, reset, watch, setValue } = useForm<UserFormValues>({
    defaultValues: {
      firstName: '',
      lastName: '',
      email: '',
      role: 'admin',
      password: '',
      confirmPassword: '',
      isActive: true,
      modulePermissions: [],
      accessProfileId: 'role_sub_admin',
    },
  });

  const firstName = watch('firstName');
  const lastName = watch('lastName');
  const email = watch('email');
  const role = watch('role');
  const passwordValue = watch('password');
  const isActive = watch('isActive');
  const modulePermissions = watch('modulePermissions');
  const accessProfileId = watch('accessProfileId');

  const teamRoles = permissionsConfig?.customProfiles ?? [];

  const selectedAssignment: RoleAssignment =
    role === 'super_admin' ? 'super_admin' : accessProfileId ?? 'role_sub_admin';

  const applyRoleAssignment = (assignment: RoleAssignment) => {
    if (assignment === 'super_admin') {
      setValue('role', 'super_admin');
      setValue('accessProfileId', null);
      setValue('modulePermissions', []);
      return;
    }

    const profile = teamRoles.find((item) => item.id === assignment);
    if (!profile) return;

    setValue('role', profile.baseRole);
    setValue('accessProfileId', profile.id);
    setValue('modulePermissions', profile.modules);
  };

  useEffect(() => {
    if (!isEdit || !user) return;

    const initialModules =
      user.modulePermissions.length > 0
        ? user.modulePermissions
        : user.effectivePermissions;

    reset({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
      password: '',
      confirmPassword: '',
      isActive: user.isActive,
      modulePermissions: user.role === 'super_admin' ? [] : initialModules,
      accessProfileId: user.accessProfileId ?? null,
    });
  }, [isEdit, reset, user]);

  useEffect(() => {
    if (isEdit || !permissionsConfig?.customProfiles.length) return;
    const defaultProfile =
      permissionsConfig.customProfiles.find((profile) => profile.id === 'role_sub_admin') ??
      permissionsConfig.customProfiles[0];

    setValue('role', defaultProfile.baseRole);
    setValue('accessProfileId', defaultProfile.id);
    setValue('modulePermissions', defaultProfile.modules);
  }, [isEdit, permissionsConfig, setValue]);

  const selectedModuleLabels = useMemo(
    () =>
      ADMIN_MODULES.filter((module) => modulePermissions.includes(module.key)).map(
        (module) => module.label,
      ),
    [modulePermissions],
  );

  const roleDisplayLabel = getRoleDisplayLabel(role, accessProfileId, teamRoles);

  const initials = useMemo(() => {
    const first = firstName.trim().charAt(0).toUpperCase();
    const last = lastName.trim().charAt(0).toUpperCase();
    if (first && last) return `${first}${last}`;
    if (first) return first;
    return '?';
  }, [firstName, lastName]);

  const displayName = useMemo(() => {
    const name = `${firstName.trim()} ${lastName.trim()}`.trim();
    return name || 'New admin user';
  }, [firstName, lastName]);

  const onSubmit = async (values: UserFormValues) => {
    if (!isEdit && values.password !== values.confirmPassword) {
      notify.error('Passwords do not match.');
      return;
    }

    if (isEdit && values.password && values.password !== values.confirmPassword) {
      notify.error('Passwords do not match.');
      return;
    }

    if (values.role !== 'super_admin' && !values.accessProfileId) {
      notify.error('Please select a team role.');
      return;
    }

    const sharedPayload = {
      firstName: values.firstName.trim(),
      lastName: values.lastName.trim(),
      email: values.email.trim(),
      role: values.role,
      isActive: values.isActive,
      modulePermissions: values.role === 'super_admin' ? [] : values.modulePermissions,
      accessProfileId: values.role === 'super_admin' ? null : values.accessProfileId,
    };

    if (isEdit) {
      if (!id) return;

      const result = await resolveMutationResult<AdminUser>(
        updateUser({
          id,
          body: {
            ...sharedPayload,
            ...(values.password ? { password: values.password } : {}),
          },
        }),
      );
      if (!result.ok) {
        notify.error(result.message);
        return;
      }

      notify.success(result.message ?? 'User updated successfully.');
      navigate(`${ROUTES.USERS}?tab=users`);
      return;
    }

    const result = await resolveMutationResult<AdminUser>(
      createUser({
        ...sharedPayload,
        password: values.password,
      }),
    );

    if (!result.ok) {
      notify.error(result.message);
      return;
    }

    notify.success(result.message ?? 'User created successfully.');
    navigate(`${ROUTES.USERS}?tab=users`);
  };

  const roleLocked = isEdit && user?.id === currentUser?.id && user?.role === 'super_admin';
  const statusLocked = isEdit && user?.id === currentUser?.id;

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <Button icon={<ArrowLeftOutlined />} onClick={() => navigate(ROUTES.USERS)}>
            Back to users
          </Button>
        }
      />

      <div className={styles.layout}>
        <div className={styles.main}>
          <div className={`${styles.panel} ${isEdit && isLoadingUser ? styles.panelLoading : ''}`}>
            <div className={styles.hero}>
              <div className={styles.heroAccent} />
              <div className={styles.heroContent}>
                <div className={styles.avatar}>{initials}</div>
                <div className={styles.heroText}>
                  <h2 className={styles.heroTitle}>{displayName}</h2>
                  <p className={styles.heroEmail}>
                    <MailOutlined />
                    {email.trim() || 'email@example.com'}
                  </p>
                  <div className={styles.heroMeta}>
                    <span className={`${styles.rolePill} ${styles[`role_${role}`]}`}>
                      {roleDisplayLabel}
                    </span>
                    <span className={`${styles.statusPill} ${isActive ? styles.statusActive : ''}`}>
                      {isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <Form layout="vertical" onFinish={handleSubmit(onSubmit)} className={styles.form}>
              <section className={styles.section}>
                <div className={styles.sectionHead}>
                  <UserOutlined className={styles.sectionIcon} />
                  <div>
                    <h3 className={styles.sectionTitle}>Personal details</h3>
                    <p className={styles.sectionDesc}>Basic profile information for this staff account</p>
                  </div>
                </div>

                <div className={styles.fieldGrid}>
                  <Form.Item label="First name" required>
                    <Controller
                      name="firstName"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => <Input {...field} placeholder="Jane" size="large" />}
                    />
                  </Form.Item>

                  <Form.Item label="Last name" required>
                    <Controller
                      name="lastName"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => <Input {...field} placeholder="Doe" size="large" />}
                    />
                  </Form.Item>

                  <Form.Item label="Email address" required className={styles.fullWidth}>
                    <Controller
                      name="email"
                      control={control}
                      rules={{ required: true }}
                      render={({ field }) => (
                        <Input
                          {...field}
                          type="email"
                          size="large"
                          prefix={<MailOutlined className={styles.inputIcon} />}
                          placeholder="jane@betiyan.com"
                        />
                      )}
                    />
                  </Form.Item>
                </div>
              </section>

              <section className={styles.section}>
                <div className={styles.sectionHead}>
                  <SafetyCertificateOutlined className={styles.sectionIcon} />
                  <div>
                    <h3 className={styles.sectionTitle}>Role &amp; access</h3>
                    <p className={styles.sectionDesc}>
                      Assign a team role like Sub Admin, Product Manager, or Sales Manager
                    </p>
                  </div>
                </div>

                <div className={styles.roleGrid}>
                  {teamRoles.map((profile) => {
                    const Icon = profileIcon(profile.name);
                    const selected = selectedAssignment === profile.id;

                    return (
                      <button
                        key={profile.id}
                        type="button"
                        className={`${styles.roleCard} ${selected ? styles.roleCardSelected : ''} ${styles[`roleCard_${profile.baseRole}`]}`}
                        disabled={roleLocked}
                        onClick={() => applyRoleAssignment(profile.id)}
                      >
                        <span className={styles.roleCardIcon}>
                          <Icon />
                        </span>
                        <span className={styles.roleCardLabel}>{profile.name}</span>
                        <span className={styles.roleCardDesc}>
                          {profile.modules.length} modules
                        </span>
                        {selected ? (
                          <CheckCircleOutlined className={styles.roleCardCheck} />
                        ) : null}
                      </button>
                    );
                  })}
                </div>

                {role === 'super_admin' ? (
                  <div className={styles.fullAccessNotice}>
                    <CrownOutlined />
                    <span>Super admins automatically receive access to all dashboard modules.</span>
                  </div>
                ) : (
                  <Controller
                    name="modulePermissions"
                    control={control}
                    render={({ field }) => (
                      <ModuleAccessPicker
                        value={field.value}
                        onChange={(modules) => {
                          field.onChange(modules);
                        }}
                      />
                    )}
                  />
                )}
              </section>

              <section className={styles.section}>
                <div className={styles.sectionHead}>
                  <LockOutlined className={styles.sectionIcon} />
                  <div>
                    <h3 className={styles.sectionTitle}>Account security</h3>
                    <p className={styles.sectionDesc}>
                      {isEdit
                        ? 'Leave blank to keep the current password unchanged'
                        : 'Set a strong password for dashboard sign-in'}
                    </p>
                  </div>
                </div>

                <div className={styles.fieldGrid}>
                  <Form.Item
                    label={isEdit ? 'New password' : 'Password'}
                    required={!isEdit}
                    className={styles.fullWidth}
                  >
                    <Controller
                      name="password"
                      control={control}
                      rules={{ required: !isEdit }}
                      render={({ field }) => (
                        <Input.Password
                          {...field}
                          size="large"
                          placeholder={isEdit ? 'Leave blank to keep current password' : 'Min 8 characters'}
                        />
                      )}
                    />
                  </Form.Item>

                  <Form.Item
                    label={isEdit ? 'Confirm new password' : 'Confirm password'}
                    required={!isEdit || Boolean(passwordValue)}
                    className={styles.fullWidth}
                  >
                    <Controller
                      name="confirmPassword"
                      control={control}
                      rules={{ required: !isEdit || Boolean(passwordValue) }}
                      render={({ field }) => (
                        <Input.Password {...field} size="large" placeholder="Re-enter password" />
                      )}
                    />
                  </Form.Item>
                </div>

                <ul className={styles.passwordHints}>
                  <li>At least 8 characters</li>
                  <li>Uppercase, lowercase, number, and special character</li>
                </ul>
              </section>

              <section className={styles.section}>
                <div className={styles.statusCard}>
                  <div className={styles.statusInfo}>
                    <span className={styles.statusTitle}>Active account</span>
                    <span className={styles.statusHint}>
                      Inactive users cannot sign in to the admin panel
                    </span>
                  </div>
                  <Controller
                    name="isActive"
                    control={control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onChange={field.onChange}
                        disabled={statusLocked}
                      />
                    )}
                  />
                </div>
              </section>

              <div className={styles.formActions}>
                <Button size="large" onClick={() => navigate(ROUTES.USERS)}>
                  Cancel
                </Button>
                <Button
                  type="primary"
                  size="large"
                  loading={isSaving}
                  onClick={handleSubmit(onSubmit)}
                >
                  {isEdit ? 'Save changes' : 'Create user'}
                </Button>
              </div>
            </Form>
          </div>
        </div>

        <aside className={styles.sidebar}>
          <div className={styles.sideCard}>
            <h3 className={styles.sideTitle}>Module access</h3>
            <p className={styles.sideDesc}>
              <strong>{modulePermissions.length}</strong> modules selected for this{' '}
              <strong>{roleDisplayLabel}</strong> account.
            </p>

            {role === 'super_admin' ? (
              <div className={styles.sideNotice}>Full unrestricted dashboard access.</div>
            ) : selectedModuleLabels.length ? (
              <ul className={styles.permissionList}>
                {selectedModuleLabels.map((label) => (
                  <li key={label}>
                    <CheckCircleOutlined />
                    <span>{label}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className={styles.sideNotice}>No modules selected yet.</div>
            )}
          </div>

          <div className={styles.sideCard}>
            <h3 className={styles.sideTitle}>Quick tips</h3>
            <ul className={styles.tipList}>
              <li>Assign the lowest role that still covers daily tasks.</li>
              <li>Only super admins can create other super admin accounts.</li>
              <li>Deactivate accounts instead of deleting when access is temporary.</li>
            </ul>
          </div>
        </aside>
      </div>
    </div>
  );
}
